/**
 * UpvoteChip — Push #28 social-style upvote counter.
 *
 * Replaces the plain numeric counter previous catalog tiles used. The chip
 * is a small rounded pill with a heart icon + count. On a successful click
 * (when the user hasn't already upvoted), the chip pops — 200ms scale to
 * 1.1x + teal flash — then settles into the upvoted state (filled heart,
 * teal-tinted bg). A second click is a no-op (server is idempotent via
 * the `upvoted_slugs` array in sys__account_user.ui_preferences —
 * Migration H, 2026-06-24).
 *
 * Visual rules:
 *   - Outlined heart + grey number = not yet upvoted by current user
 *   - Filled heart + teal-tinted bg + teal number = already upvoted
 *   - 200ms pop animation fires when the counter increments
 *   - Disabled when `loading` is true
 *
 * The count is the value of mcp__connector_template.upvote_count /
 * llm__model_template.upvote_count read directly from the template row by
 * /api/catalog/all. Global signal across all workspaces — community moat
 * for "what should ship next." (Prompts moved to Group 2 BYO in Push 4-1c-b
 * Migration 0144, so no prompt-template upvotes.)
 */

import { useEffect, useState } from 'react';

interface UpvoteChipProps {
  /** Current upvote count for this target (aggregate across the account). */
  count: number;
  /** True if the current user has already upvoted this target. */
  upvoted: boolean;
  /** Click handler. Caller is responsible for the network call + state
   *  rollback on failure. */
  onClick: () => void;
  /** Disabled state (in-flight). */
  loading?: boolean;
  /** Optional aria-label override (default: "Upvote — N people interested"). */
  ariaLabel?: string;
}

export function UpvoteChip({ count, upvoted, onClick, loading, ariaLabel }: UpvoteChipProps) {
  const [popping, setPopping] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  // When the count goes up, trigger the pop animation. We compare against
  // the previous-render count, not just `count > 0`, so the animation only
  // fires on actual increment (not e.g. an initial mount with count = 5).
  useEffect(() => {
    if (count > prevCount) {
      setPopping(true);
      const t = window.setTimeout(() => setPopping(false), 200);
      return () => window.clearTimeout(t);
    }
    setPrevCount(count);
    return undefined;
  }, [count, prevCount]);

  const label = ariaLabel ?? (count === 1 ? '1 person interested. Upvote' : `${count} people interested. Upvote`);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading === true}
      className={`upvote-chip${upvoted ? ' is-upvoted' : ''}${popping ? ' is-pop' : ''}`}
      aria-label={label}
      aria-pressed={upvoted}
      title={upvoted ? "You upvoted this" : "Upvote. Let your team know you'd use this"}
    >
      <svg
        className="upvote-chip-icon"
        width="12"
        height="12"
        viewBox="0 0 16 16"
        aria-hidden="true"
      >
        {upvoted ? (
          // Filled heart
          <path
            fill="currentColor"
            d="M8 13.5l-.85-.78C3.4 9.36 1 7.28 1 4.75 1 2.85 2.42 1.5 4.25 1.5c1.04 0 2.04.48 2.75 1.24A3.74 3.74 0 0 1 9.75 1.5C11.58 1.5 13 2.85 13 4.75c0 2.53-2.4 4.61-6.15 7.97L8 13.5z"
          />
        ) : (
          // Outlined heart
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            d="M8 13.5l-.85-.78C3.4 9.36 1 7.28 1 4.75 1 2.85 2.42 1.5 4.25 1.5c1.04 0 2.04.48 2.75 1.24A3.74 3.74 0 0 1 9.75 1.5C11.58 1.5 13 2.85 13 4.75c0 2.53-2.4 4.61-6.15 7.97L8 13.5z"
          />
        )}
      </svg>
      <span className="upvote-chip-count">{count}</span>
    </button>
  );
}
